import { useCallback, useMemo } from "react";
import { Box, Flex, Heading, Text, Spinner } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useController, UseFormReturn } from "react-hook-form";
import type { SkuInputs } from "components/Products/SkuPanel/utils";
import SkuOrderingPlatform, {
  skuOrderingPlatformFragment,
} from "./SkuOrderingPlatform";
import AddNewOrderingPlatform from "./AddNewOrderingPlatform";

export { skuOrderingPlatformFragment };

type Props = {
  formContext: UseFormReturn<SkuInputs>;
  isLoading?: boolean;
};

export default function SkuOrderingPlatforms({
  formContext,
  isLoading,
}: Props) {
  const { control } = formContext;
  const {
    field: { value, onChange },
  } = useController({ control, name: "orderingPlatformData" });

  const existingIds = useMemo(
    () => value?.map((op) => op.orderingPlatformId),
    [value],
  );

  const handleDelete = useCallback(
    (orderingPlatformId: string) => {
      onChange(
        value?.filter((op) => op.orderingPlatformId !== orderingPlatformId) ||
          [],
      );
    },
    [value, onChange],
  );

  const addNewProps = {
    existingIds,
    onSelect: (orderingPlatformId: string) => {
      const formValues = formContext.getValues();
      onChange([
        ...(formValues.orderingPlatformData || []),
        { orderingPlatformId },
      ]);
    },
  };

  return (
    <Box mb="15rem">
      <Flex alignItems="center">
        <Heading as="h2" size="sm" color="magenta.veryDark">
          Wholesalers
        </Heading>
        {!!value?.length && (
          <AddNewOrderingPlatform
            {...addNewProps}
            buttonProps={{
              ml: "auto",
              variant: "outline",
              colorScheme: "lightPinkButton",
              size: "xs",
              leftIcon: <AddIcon boxSize=".75em" />,
            }}
          />
        )}
      </Flex>

      <Box as="ul">
        {value?.map(
          (op, index) =>
            op.orderingPlatformId && (
              <SkuOrderingPlatform
                // eslint-disable-next-line react/no-array-index-key
                key={`${op.orderingPlatformId}-${index}`}
                {...{ formContext, index }}
                onDelete={handleDelete}
              />
            ),
        )}
      </Box>

      {isLoading && !value?.length && (
        <Box textAlign="center" p={10}>
          <Spinner size="xl" color="gray.400" />
        </Box>
      )}

      {!value?.length && !isLoading && (
        <Box
          borderWidth={1}
          borderColor="gray.300"
          p={5}
          borderRadius="md"
          mt={4}
        >
          <Text
            mt={-1}
            mb={2}
            fontSize="sm"
            textAlign="center"
            color="gray.600"
          >
            No wholesalers added
          </Text>
          <AddNewOrderingPlatform
            {...addNewProps}
            buttonProps={{
              colorScheme: "lightPinkButton",
              size: "sm",
              children: "Add Wholesaler",
              display: "block",
              m: "0 auto",
            }}
          />
        </Box>
      )}
    </Box>
  );
}
